import {
  usePeopleSearch,
  useServiceAccess,
  useServiceTypePlace,
  useServicesPerson,
  usePlanEditor,
  usePlanSections,
  usePlanScheduling,
  usePlanTeams,
  useCreateSeries,
  useMyPlanPlaces,
  usePlanTitleEditor,
  seriesArtUrl,
  useSeriesList,
  useServiceTypes,
  useSession,
  useSpotifyPanel,
  notify,
  useUndoableRemove,
  type Plan,
  type ServicesPerson,
} from '@church/data'
import { orderedCategoryNotes } from '@church/domain'
import {
  faBell,
  faCheck,
  faCopy,
  faEllipsisVertical,
  faBookOpen,
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
  faCircleXmark,
  faFloppyDisk,
  faHourglassHalf,
  faLayerGroup,
  faListOl,
  faUsers,
  faClock,
  faFolder,
  faPen,
  faSpotifyBrands,
  faMinus,
  faPlus,
  faTrashCan,
  faUserMinus,
  faXmark,
  faPhone,
  faEnvelope,
  faLocationDot,
} from '@church/ui-icons'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { z } from 'zod'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  ErrorNote,
  Field,
  Icon,
  IconButton,
  Input,
  listRow,
  ListCard,
  ListTitle,
  PanelSection,
  pressable,
  InlineSpinner,
  Select,
  SidePanel,
  Spinner,
  Textarea,
  MOBILE_QUERY,
  PHONE_QUERY,
  SectionTitle,
  ShownSection,
  useEdgeFade,
  useMediaQuery,
} from '../components/ui.tsx'
import { SeriesChip, SeriesSection } from '../components/services/Series.tsx'
import { PlanFilesCard } from '../components/services/PlanFiles.tsx'
import { SermonCard } from '../components/services/Sermon.tsx'
import { SermonHubSection } from '../components/services/SermonHub.tsx'
import { IdeasList } from '../components/services/SermonIdeas.tsx'
import { PositionGlyph } from '../components/services/PositionIcon.tsx'
import { LiveBar, LiveButton } from '../components/services/LiveButton.tsx'
import { SpotifyPanel } from '../components/services/SpotifyPanel.tsx'
import { PlanTimesCard, servedLabel, TimeChoice } from '../components/services/PlanTimes.tsx'
import { NeededAvatar, PersonAvatar, responseOf } from '../components/PersonAvatar.tsx'
import { ProfileRow } from '../components/services/PersonLink.tsx'
import { SaveFailure } from '../components/services/SaveStatus.tsx'
import { RunningOrderCard } from '../components/services/RunningOrder.tsx'
import { SaveAsTemplatePanel } from '../components/services/Templates.tsx'
import { ActionMenu } from '../components/ActionMenu.tsx'
import { DeclinePanel } from '../components/services/DeclinePanel.tsx'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'
import { folderCrumbs } from '../lib/services-crumbs.ts'
import { useTabParam } from '../lib/tabs.ts'
import { switchSection } from '../lib/view-transition.ts'
import { useTintFromImage } from '../lib/top-bar.ts'

/**
 * The plan editor. The running order reads at a glance — start time, title,
 * key, length — and choosing an item opens a panel with everything about it:
 * key, length and phase, notes, its song, moving it, removing it. Drag a
 * row (desktop) or use Move up/down (the panel, any screen) and every time
 * below it moves too — the same `runningTimes` function on screen as on the
 * server, so the preview is the answer, not a guess.
 */
export const Route = createFileRoute('/_authed/_shell/services/plans/$id')({
  // ?tab=: the section a phone or tablet shows (Order, Teams…). Sermon was a
  // section of its own; it is part of Series now (an old link lands there).
  validateSearch: z.object({
    // The sermon is in the Series section now: an old ?tab=Sermon lands there.
    tab: z.preprocess(
      (tab) => (tab === 'Sermon' ? 'Series' : tab),
      z.enum(['Order', 'Teams', 'Files', 'Times', 'Series']).optional(),
    ),
  }),
  component: PlanEditorScreen,
})

const prettyDate = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

/** A plan's sections, as pills on a phone or tablet (and in that order in a computer's column). */
const PLAN_SECTIONS = ['Order', 'Teams', 'Files', 'Times', 'Series'] as const
const SECTION_ICONS = {
  Order: faListOl,
  Teams: faUsers,
  Times: faClock,
  Files: faFolder,
  Series: faLayerGroup,
} as const

/** The plan's series art, which colours the top bar. */
const planArt = (plan: Plan | undefined) =>
  plan?.series?.hasArt === true ? seriesArtUrl(plan.series.id, plan.series.artVersion) : null

function PlanEditorScreen() {
  const { id } = Route.useParams()
  const editor = usePlanEditor(id)
  const [spotifyOpen, setSpotifyOpen] = useState(false)
  const access = useServiceAccess()
  const { me } = useSession()
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  // Order, times, notes and files: Editor on the plan's service type (docs/13-ACCESS.md).
  const editable = editor.plan ? access.can(editor.plan.serviceTypeId, 'editor') : false
  // The plan's playlist, in ForHim's Spotify account: anyone who sees the plan, Editors refresh.
  const spotify = useSpotifyPanel(id, {
    canRefresh: editable,
    operator: me?.platformOperator ?? false,
  })
  // Phones and tablets show one section at a time, chosen from pills.
  const mobile = useMediaQuery(MOBILE_QUERY)
  const [section, setSection] = useTabParam(PLAN_SECTIONS, 'Order')
  const pills = useEdgeFade()
  // Every section's data comes with the plan: a pill shows its section at once.
  usePlanSections(id, editor.plan?.series?.id ?? null)
  useTintFromImage(planArt(editor.plan))
  // Plans › Main campus › Sunday Morning › Plan: its folders lead back to
  // their levels, its service to that service's plans.
  const place = useServiceTypePlace(editor.plan?.serviceTypeId ?? null)
  useBreadcrumbs([
    ...folderCrumbs(place.folders),
    ...(editor.plan
      ? [
          {
            label: editor.plan.serviceTypeName,
            href: `/services/types/${editor.plan.serviceTypeId}`,
          },
          // The plan's own title is right under the top bar: the crumb says what this is.
          { label: 'Plan' },
        ]
      : []),
  ])

  if (editor.isLoading) return <Spinner />
  if (!editor.plan) return <ErrorNote error={editor.error} />
  const plan = editor.plan

  const orderCard = (
    <RunningOrderCard
      order={{ kind: 'plan', id: plan.id }}
      editable={editable}
      times={plan.times}
      liveItemId={plan.liveItemId}
    />
  )

  // The Series section: its series, then the plan's sermon and all that grows from it.
  const seriesSection = (
    <>
      <SeriesSection plan={plan} />
      {!plan.series && editable && (
        <Button
          variant="secondary"
          icon={faLayerGroup}
          className="w-full"
          onClick={() => setDetailsOpen(true)}
        >
          Add to a series
        </Button>
      )}
      {/* The sermon belongs to its series: it and all that grows from it (docs/15). */}
      <SermonCard plan={plan} />
      <SermonHubSection plan={plan} />
    </>
  )

  return (
    <div className="space-y-6">
      <PlanHeader
        plan={plan}
        editable={editable}
        seriesChip={!mobile}
        onEdit={() => setDetailsOpen(true)}
        // Templates are made by the service type's Administrators.
        onSaveTemplate={
          access.can(plan.serviceTypeId, 'administrator') ? () => setSavingTemplate(true) : null
        }
        onLive={(itemId) => editor.goLive.mutate(itemId)}
        onSpotify={spotify.showButton ? () => setSpotifyOpen(true) : null}
      />
      <SpotifyPanel panel={spotify} open={spotifyOpen} onClose={() => setSpotifyOpen(false)} />
      <AskedBanner planId={plan.id} date={plan.date} />
      <SaveFailure order={{ kind: 'plan', id: plan.id }} />
      {access.can(plan.serviceTypeId, 'administrator') && (
        <SaveAsTemplatePanel
          plan={plan}
          open={savingTemplate}
          onClose={() => setSavingTemplate(false)}
        />
      )}
      {editable && (
        <PlanDetailsPanel
          key={plan.id}
          plan={plan}
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
        />
      )}
      {/* Phones and tablets: one section at a time, from pills under the
          header (sticky under the top bar). Desktop: everything, the order on
          the right and the rest in a column on the left. */}
      {mobile && (
        // Closer under the header than the page's usual gap: as far below
        // the art as above the content.
        <nav
          aria-label="Plan sections"
          className="plan-pills sticky under-bar z-3 -mx-gutter -mt-4 mb-2! bg-surface-base"
        >
          <div
            ref={pills.ref}
            className={`tabs-fade flex gap-2 overflow-x-auto px-3 py-2 no-scrollbar ${pills.edge}`}
          >
            {PLAN_SECTIONS.map((name) => (
              <button
                key={name}
                type="button"
                aria-pressed={section === name}
                onClick={(event) => {
                  // A pill half under the fade comes fully into view.
                  event.currentTarget.scrollIntoView({
                    block: 'nearest',
                    inline: 'nearest',
                    behavior: 'smooth',
                  })
                  switchSection(() => setSection(name))
                }}
                className={`${pressable} inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-pill leading-none font-semibold transition-colors duration-(--duration-base) ${
                  section === name
                    ? 'bg-brand-solid text-brand-on-solid'
                    : 'bg-surface-heading text-content-default hover:bg-surface-section'
                }`}
              >
                <Icon icon={SECTION_ICONS[name]} className="size-3.5" />
                {name}
              </button>
            ))}
          </div>
        </nav>
      )}
      {mobile ? (
        <ShownSection value={section}>
          <div className="plan-sections space-y-6">
            {section === 'Order' && (
              <>
                {orderCard}
                <PlanNotesCard plan={plan} />
              </>
            )}
            {section === 'Teams' && <TeamPanel planId={plan.id} />}
            {section === 'Files' && (
              <PlanFilesCard
                planId={plan.id}
                serviceTypeId={plan.serviceTypeId}
                series={plan.series}
              />
            )}
            {section === 'Times' && <PlanTimesCard plan={plan} editable={editable} />}
            {section === 'Series' && seriesSection}
          </div>
        </ShownSection>
      ) : (
        <div className="grid grid-cols-plan items-start gap-6">
          {/* The sections in the pills' order: the order's notes, then Teams,
              Files, Times, and the Series section's sermon (the series
              itself is a chip by the title). */}
          <div className="min-w-0 space-y-6">
            <PlanNotesCard plan={plan} />
            <TeamPanel planId={plan.id} />
            <PlanFilesCard
              planId={plan.id}
              serviceTypeId={plan.serviceTypeId}
              series={plan.series}
            />
            <PlanTimesCard plan={plan} editable={editable} />
            <SermonCard plan={plan} />
            <IdeasList plan={plan} pageLink />
          </div>
          <div className="min-w-0">{orderCard}</div>
        </div>
      )}

      {editable && plan.liveItemId && (
        <>
          {/* Room for the live bar under the last row on a phone. */}
          <div aria-hidden className="h-16 sm:hidden" />
          <LiveBar
            liveItemId={plan.liveItemId}
            items={plan.items}
            onMove={(itemId) => editor.goLive.mutate(itemId)}
          />
        </>
      )}
    </div>
  )
}

/**
 * The plan's title big (what people call this Sunday — the service type when
 * it has none), the date and the rest smaller under it. Above the title, on
 * the right, the buttons — Spotify, Go live, the Music Stand — and under
 * them, on a row of its own, the plan's ⋯ menu (Edit, Save as template), so a
 * phone keeps the title's width. On a computer its Editors change the title
 * where it stands: a click makes it a field (Enter or leaving it saves,
 * Escape puts it back). A plan in a series with art wears it as a cover
 * behind all of it.
 */
function PlanHeader({
  plan,
  editable,
  seriesChip,
  onEdit,
  onSaveTemplate,
  onLive,
  onSpotify,
}: {
  plan: Plan
  editable: boolean
  /** The plan's ⋯ actions: Save as template (its service type's Administrators); null, none. */
  onSaveTemplate: (() => void) | null
  /** A computer: the series (no column of its own there) as a chip by the title. */
  seriesChip: boolean
  onEdit: () => void
  onLive: (itemId: string | null) => void
  /** Null: no Spotify playlist can be had here (none set up, no room) — no button. */
  onSpotify: (() => void) | null
}) {
  const navigate = useNavigate()
  const { serving } = useMyPlanPlaces(plan.id)
  const servingAs = [...new Set(serving.map((p) => p.positionName))].join(', ')
  const title = usePlanTitleEditor(plan)
  // The title edits where it stands on a computer; a phone opens the panel.
  const phone = useMediaQuery(PHONE_QUERY)
  const inline = editable && !phone
  const art = planArt(plan)
  // Over the art the text is white on a dark scrim, in either theme.
  const onArt = art !== null
  const button = onArt
    ? 'text-white hover:bg-black/30 disabled:opacity-40'
    : 'disabled:opacity-40 disabled:hover:bg-transparent'
  const step = (planId: string | null) => {
    if (planId) void navigate({ to: '/services/plans/$id', params: { id: planId } })
  }
  const actions = [
    ...(editable ? [{ label: 'Edit plan details', icon: faPen, onSelect: onEdit }] : []),
    ...(onSaveTemplate
      ? [{ label: 'Save as template', icon: faCopy, onSelect: onSaveTemplate }]
      : []),
  ]
  const heading = `min-w-0 text-2xl font-bold break-words sm:text-3xl ${
    onArt ? 'text-white' : 'text-content-default'
  }`

  return (
    <header
      className={`vt-plan-header ${
        onArt
          ? 'relative -mx-gutter -mt-6 overflow-hidden sm:mx-0 sm:mt-0 sm:rounded-card'
          : 'relative pb-2'
      }`}
    >
      {art && (
        <>
          {/* Blurred and darkened: the art sets the mood, the words stay legible. */}
          <img
            src={art}
            alt=""
            className="absolute inset-0 size-full scale-105 object-cover blur-art"
          />
          <div className="absolute inset-0 bg-linear-to-t from-black/90 via-black/65 to-black/45" />
        </>
      )}
      <div
        className={
          onArt
            ? // The gap leaves the ⋯ row its room under the buttons.
              'relative flex min-h-56 flex-col justify-between gap-10 px-4 pt-2 pb-5 sm:min-h-64 sm:px-6 sm:pt-4 sm:pb-6'
            : 'space-y-1'
        }
      >
        <div className="flex items-center justify-end gap-2">
          <div className="relative -mr-2 flex shrink-0 items-center gap-0.5 sm:mr-0 sm:gap-1">
            {onSpotify && (
              <IconButton
                icon={faSpotifyBrands}
                label="Spotify playlist"
                className={button}
                onClick={onSpotify}
              />
            )}
            <LiveButton
              liveItemId={plan.liveItemId}
              items={plan.items}
              canLead={editable}
              onMove={onLive}
              {...(onArt ? { className: 'text-white hover:bg-black/30' } : {})}
            />
            <Link
              to="/stand/$planId"
              params={{ planId: plan.id }}
              aria-label="Music Stand"
              title="Music Stand"
              className="ml-1 inline-flex size-10 items-center justify-center gap-2 rounded-lg bg-brand-solid text-sm font-medium text-brand-on-solid no-underline hover:bg-brand-solid-hover sm:w-auto sm:px-4"
            >
              <Icon icon={faBookOpen} />
              <span className="hidden sm:inline">Music Stand</span>
            </Link>
            {/* The plan's ⋯ on a row of its own under the buttons, at their
                right: in the room the header already has (it adds no height). */}
            {actions.length > 0 && (
              <div className="absolute top-full right-0 flex justify-end">
                <ActionMenu
                  label="More plan actions"
                  title="Plan"
                  trigger={<Icon icon={faEllipsisVertical} />}
                  triggerClassName={`inline-flex size-icon-button shrink-0 items-center justify-center rounded-control ${
                    onArt
                      ? 'text-white hover:bg-black/30'
                      : 'text-content-muted hover:bg-surface-sunken hover:text-content-default'
                  }`}
                  entries={actions}
                />
              </div>
            )}
          </div>
        </div>
        {/* Without art the title starts right under the buttons: it keeps
            clear of the ⋯ beside it. */}
        <div className={`min-w-0 ${!onArt && actions.length > 0 ? 'pr-12' : ''}`}>
          <p
            className={`truncate text-xs font-semibold tracking-wide uppercase ${
              onArt ? 'text-white/80' : 'text-content-muted'
            }`}
          >
            {plan.serviceTypeName}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            {inline && title.editing ? (
              <div className={`${heading} max-w-full`}>
                <input
                  autoFocus
                  aria-label="Plan title"
                  value={title.draft}
                  maxLength={200}
                  placeholder={plan.serviceTypeName}
                  onChange={(event) => title.change(event.target.value)}
                  onFocus={(event) => event.currentTarget.select()}
                  onBlur={title.commit}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      title.commit()
                    } else if (event.key === 'Escape') {
                      event.preventDefault()
                      title.cancel()
                    }
                  }}
                  // As wide as what it holds (with room to type), so the series
                  // chip stays by it. In ch, not field-sizing: Chrome's sizes
                  // it a caret short, so the text scrolls; Firefox has none.
                  style={{ width: `${Math.max(title.draft.length, 10) + 2}ch` }}
                  className={`-mx-1.5 max-w-full rounded-control px-1.5 outline-none ring-2 ${
                    onArt
                      ? 'bg-black/35 ring-white/40 placeholder:text-white/50'
                      : 'bg-surface-raised ring-brand-soft placeholder:text-content-subtle'
                  }`}
                />
              </div>
            ) : (
              <h1 className={heading}>
                {inline ? (
                  <button
                    type="button"
                    title="Change the title"
                    onClick={title.start}
                    className={`-mx-1.5 cursor-text rounded-control px-1.5 text-left transition-colors duration-(--duration-fast) ${
                      onArt ? 'hover:bg-black/30' : 'hover:bg-surface-sunken'
                    }`}
                  >
                    {title.shown}
                  </button>
                ) : (
                  title.shown
                )}
                {title.saving && (
                  <InlineSpinner
                    className={`ml-2 inline-block size-4 align-middle ${onArt ? 'text-white' : ''}`}
                  />
                )}
                {/* You said yes: a quiet tick by the title, not another banner. */}
                {servingAs && (
                  <span
                    role="img"
                    aria-label={`You're serving: ${servingAs}`}
                    title={`You're serving: ${servingAs}`}
                    className={`ml-2 inline-flex size-7 items-center justify-center rounded-full align-middle ${
                      onArt ? 'bg-white/20 text-white' : 'bg-success-soft text-success-solid'
                    }`}
                  >
                    <Icon icon={faCircleCheck} className="size-4" />
                  </span>
                )}
              </h1>
            )}
            {seriesChip && plan.series && <SeriesChip plan={plan} onArt={onArt} />}
          </div>
          {/* The day, at a glance: "Oct 25", with the plans either side of it a
              step away. Times and length are in the Times section. */}
          <div className="mt-1 -ml-2 flex items-center gap-1">
            <IconButton
              icon={faChevronLeft}
              label={`Previous ${plan.serviceTypeName} plan`}
              className={`size-8! ${button}`}
              disabled={!plan.previousPlanId}
              onClick={() => step(plan.previousPlanId)}
            />
            <IconButton
              icon={faChevronRight}
              label={`Next ${plan.serviceTypeName} plan`}
              className={`size-8! ${button}`}
              disabled={!plan.nextPlanId}
              onClick={() => step(plan.nextPlanId)}
            />
            <p
              className={`ml-1 text-xl font-semibold tabular-nums ${
                onArt ? 'text-white/70' : 'text-content-muted'
              }`}
            >
              <time dateTime={plan.date} title={prettyDate(plan.date)}>
                {shortDate(plan.date)}
              </time>
            </p>
          </div>
          {editable && !plan.title && !title.editing && !title.saving && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {
                <button
                  type="button"
                  onClick={inline ? title.start : onEdit}
                  className={`inline-flex items-center gap-1.5 text-sm font-medium ${
                    onArt ? 'text-white' : 'text-content-brand'
                  }`}
                >
                  <Icon icon={faPlus} className="size-3" />
                  Add a title
                </button>
              }
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

/**
 * Asked to serve on this plan and not answered yet: the question at the top,
 * the answer one tap away. Accepting shows at once and says what comes next
 * (the Music Stand); Reject takes a reason in a tap. A refusal puts the
 * question back, with why, right here.
 */
function AskedBanner({ planId, date }: { planId: string; date: string }) {
  const { asked, respond } = useMyPlanPlaces(planId)
  const navigate = useNavigate()
  const [declining, setDeclining] = useState(false)
  // The positions asked, kept for the panel and the notice once answered.
  const [shownPositions, setShownPositions] = useState('')
  const positions = new Intl.ListFormat(undefined, { type: 'conjunction' }).format([
    ...new Set(asked.map((p) => p.positionName)),
  ])
  if (positions && positions !== shownPositions) setShownPositions(positions)
  if (asked.length === 0 && !declining) return null

  const accept = () =>
    respond.mutate(
      { accept: true },
      {
        onSuccess: () =>
          notify({
            title: `You’re serving as ${positions}`,
            description: `${prettyDate(date)}. The scheduler can see you said yes.`,
            action: {
              label: 'Open Music Stand',
              run: () => void navigate({ to: '/stand/$planId', params: { planId } }),
            },
          }),
      },
    )

  return (
    <>
      {asked.length > 0 && (
        <section
          aria-label="Asked to serve"
          className="flex flex-wrap items-center gap-x-3 gap-y-3 rounded-card border border-border-brand bg-brand-soft px-4 py-3"
        >
          <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-solid text-brand-on-solid">
            <Icon icon={faBell} />
          </span>
          <p className="min-w-0 flex-1 text-base text-content-default">
            You’re scheduled as <strong className="font-semibold">{positions}</strong>
          </p>
          <div className="flex w-full gap-2 sm:w-auto">
            <Button
              variant="danger"
              icon={faXmark}
              className="min-h-11 flex-1 sm:flex-none"
              disabled={respond.isPending}
              onClick={() => {
                respond.reset()
                setDeclining(true)
              }}
            >
              Reject
            </Button>
            <Button
              variant="success"
              icon={faCheck}
              className="min-h-11 flex-1 sm:flex-none"
              pending={respond.isPending && respond.variables?.accept === true}
              pendingLabel="Accepting…"
              disabled={respond.isPending}
              onClick={accept}
            >
              Accept
            </Button>
          </div>
          {!declining && respond.error && (
            <div className="w-full">
              <ErrorNote
                error={respond.error}
                onRetry={accept}
                safe="Your answer wasn’t sent: the request is still waiting for you."
              />
            </div>
          )}
        </section>
      )}
      <DeclinePanel
        open={declining}
        onOpenChange={(next) => {
          if (!next) setDeclining(false)
        }}
        description={shownPositions}
        pending={respond.isPending}
        error={declining ? respond.error : null}
        onDecline={(reason, done) =>
          respond.mutate(
            { accept: false, reason },
            {
              onSuccess: () => {
                done()
                setDeclining(false)
                notify({
                  title: `Rejected ${shownPositions}`,
                  description: `${prettyDate(date)}. The scheduler sees your reason: “${reason}”.`,
                })
              },
            },
          )
        }
      />
    </>
  )
}

const NEW_SERIES = 'new'

/**
 * The plan's own details: its title, date, start time, notes, and the series
 * it belongs to — an existing one, a new one made here, or none.
 */
function PlanDetailsPanel({
  plan,
  open,
  onClose,
}: {
  plan: Plan
  open: boolean
  onClose: () => void
}) {
  const editor = usePlanEditor(plan.id)
  const navigate = useNavigate()
  const { data: seriesList } = useSeriesList()
  const { data: serviceTypes } = useServiceTypes()
  const createSeries = useCreateSeries()
  const formId = useId()
  const [title, setTitle] = useState(plan.title ?? '')
  const [serviceTypeId, setServiceTypeId] = useState(plan.serviceTypeId)
  const [date, setDate] = useState(plan.date)
  const [startTime, setStartTime] = useState(plan.startTime)
  const [notes, setNotes] = useState(plan.notes ?? '')
  const [categoryNotes, setCategoryNotes] = useState<Record<string, string>>(plan.categoryNotes)
  const [seriesId, setSeriesId] = useState(plan.series?.id ?? '')
  const [newSeries, setNewSeries] = useState('')
  const creating = seriesId === NEW_SERIES
  // A fresh form each time it opens, from the plan as it is now (it stays
  // mounted, so it can slide in and out).
  const [openedFor, setOpenedFor] = useState(open)
  if (open !== openedFor) {
    setOpenedFor(open)
    if (open) {
      setTitle(plan.title ?? '')
      setServiceTypeId(plan.serviceTypeId)
      setDate(plan.date)
      setStartTime(plan.startTime)
      setNotes(plan.notes ?? '')
      setCategoryNotes(plan.categoryNotes)
      setSeriesId(plan.series?.id ?? '')
      setNewSeries('')
    }
  }

  const save = (chosenSeries: string | null) =>
    editor.updatePlan.mutate(
      {
        title: title.trim() || null,
        date,
        startTime,
        notes: notes.trim() || null,
        // Every category shown: an emptied box clears its note.
        categoryNotes: Object.fromEntries(
          [...new Set([...plan.noteCategories, ...Object.keys(plan.categoryNotes)])].map(
            (category) => [category, categoryNotes[category]?.trim() ?? ''],
          ),
        ),
        seriesId: chosenSeries,
        ...(serviceTypeId !== plan.serviceTypeId && { serviceTypeId }),
      },
      { onSuccess: onClose },
    )

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Plan details"
      description={`${plan.serviceTypeName} · ${shortDate(plan.date)}`}
      primary={
        <Button
          type="submit"
          form={formId}
          icon={faFloppyDisk}
          className="py-1.5"
          pending={editor.updatePlan.isPending || createSeries.isPending}
          disabled={!date || !startTime || (creating && !newSeries.trim())}
        >
          Save
        </Button>
      }
      footer={
        <>
          <ConfirmButton
            label="Delete plan"
            icon={faTrashCan}
            title={`Delete “${plan.title ?? plan.serviceTypeName}”, ${prettyDate(plan.date)}?`}
            description={
              plan.people.length > 0
                ? `${plan.people.length} ${plan.people.length === 1 ? 'person comes' : 'people come'} off it and are not told — let them know yourself. Its uploaded files are deleted.`
                : 'Its running order and uploaded files go with it.'
            }
            confirmLabel="Delete"
            pendingLabel="Deleting…"
            variant="quietDanger"
            onConfirm={() =>
              editor.deletePlan.mutateAsync(undefined).then(() => {
                onClose()
                void navigate({ to: '/services/plans' })
              })
            }
          />
          {plan.series && (
            <Button
              variant="ghost"
              icon={faLayerGroup}
              disabled={editor.updatePlan.isPending}
              onClick={() => {
                setSeriesId('')
                save(null)
              }}
            >
              Remove from series
            </Button>
          )}
        </>
      }
    >
      <form
        id={formId}
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (!creating) return save(seriesId || null)
          createSeries.mutate(
            { name: newSeries.trim(), startsOn: date },
            { onSuccess: (created) => save(created.id) },
          )
        }}
      >
        <Field label="Title">
          <Input
            value={title}
            placeholder={plan.serviceTypeName}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>
        {serviceTypes && serviceTypes.length > 1 && (
          <Field label="Service">
            <Select
              value={serviceTypeId}
              onChange={(event) => setServiceTypeId(event.target.value)}
            >
              {serviceTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date">
            <Input
              type="date"
              value={date}
              required
              onChange={(event) => setDate(event.target.value)}
            />
          </Field>
          <Field label="First service">
            <Input
              type="time"
              value={startTime}
              required
              onChange={(event) => setStartTime(event.target.value)}
            />
          </Field>
        </div>
        <Field label="Series">
          <Select value={seriesId} onChange={(event) => setSeriesId(event.target.value)}>
            <option value="">No series</option>
            {seriesList?.map((series) => (
              <option key={series.id} value={series.id}>
                {series.name}
              </option>
            ))}
            <option value={NEW_SERIES}>+ New series…</option>
          </Select>
        </Field>
        {creating && (
          <Field label="New series name">
            <Input
              value={newSeries}
              autoFocus
              placeholder="e.g. Grace upon grace"
              onChange={(event) => setNewSeries(event.target.value)}
            />
          </Field>
        )}
        <Field label="Notes">
          <Textarea
            rows={3}
            value={notes}
            placeholder="For everyone on the plan"
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
        {[...new Set([...plan.noteCategories, ...Object.keys(plan.categoryNotes)])].map(
          (category) => (
            <Field key={category} label={`${category} notes`}>
              <Textarea
                rows={2}
                value={categoryNotes[category] ?? ''}
                placeholder={`For ${category.toLowerCase()}`}
                onChange={(event) =>
                  setCategoryNotes((current) => ({ ...current, [category]: event.target.value }))
                }
              />
            </Field>
          ),
        )}
        <ErrorNote
          error={editor.updatePlan.error ?? createSeries.error ?? editor.deletePlan.error}
        />
      </form>
    </SidePanel>
  )
}

/** "5 Oct", or "5 Oct 2024" when it isn't this year. */
const shortDate = (date: string) => {
  const day = new Date(`${date}T12:00:00Z`)
  return day.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(day.getUTCFullYear() !== new Date().getFullYear() && { year: 'numeric' }),
  })
}

/**
 * The plan's notes for everyone to read: the general one, then each
 * category's (Vocals, Lighting…). Changed under the plan's details.
 */
function PlanNotesCard({ plan }: { plan: Plan }) {
  const notes = orderedCategoryNotes(plan.categoryNotes, plan.noteCategories)
  if (!plan.notes && notes.length === 0) return null
  return (
    <ListCard title="Notes">
      <dl className="divide-y divide-border-subtle">
        {plan.notes && (
          <div className={listRow}>
            <dd className="text-base whitespace-pre-line text-content-default">{plan.notes}</dd>
          </div>
        )}
        {notes.map(({ category, text }) => (
          <div key={category} className={listRow}>
            <dt className="text-sm font-medium text-content-muted">{category}</dt>
            <dd className="text-base whitespace-pre-line text-content-default">{text}</dd>
          </div>
        ))}
      </dl>
    </ListCard>
  )
}

/**
 * Who serves, team by team — the teams you are on first, then the rest
 * alphabetically (`usePlanTeams`) — each team its own card. A team's title
 * bar counts who said yes, who has not answered and who said no. Each
 * position is a short tinted row with its icon on a tile; its people sit
 * indented under it, each avatar ringed in their answer, and a place still
 * open is a row of its own: an empty dashed circle with how many, Needed.
 * Choosing a position (or a Needed row) asks someone for it; choosing a
 * person opens reminding them and taking them off — on the teams they
 * schedule (a team leader: their own teams, docs/14).
 */
function TeamPanel({ planId }: { planId: string }) {
  const editor = usePlanEditor(planId)
  const roster = usePlanTeams(planId)
  const [asking, setAsking] = useState<string | null>(null)
  const [personOpen, setPersonOpen] = useState(false)
  const [personId, setPersonId] = useState<string | null>(null)
  const plan = editor.plan
  if (!plan || !roster) return null
  const person = plan.people.find((p) => p.id === personId)

  return (
    <section aria-label="Teams" className="space-y-3 sm:space-y-4">
      <SectionTitle title="Teams" />
      {roster.groups.length === 0 && (
        <Card className="p-4 text-sm text-content-muted">
          No teams yet: make them under Services › Teams, and they show here for every plan.
        </Card>
      )}
      {roster.groups.map(({ team, mine, schedules: editable, positions, counts }) => (
        <Card bleed lines edgeless key={team.id}>
          <ListTitle title={team.name} small>
            {mine && <Chip tone="brand">Your team</Chip>}
            <TeamCounts counts={counts} />
          </ListTitle>
          {positions.map((p) => (
            <section key={p.id} aria-label={p.name}>
              <button
                type="button"
                disabled={!editable}
                onClick={() => setAsking(p.id)}
                aria-label={
                  editable
                    ? `${p.name}${p.open > 0 ? `, ${p.open} needed` : ''}: ask someone`
                    : undefined
                }
                // Shorter than a row: it heads the rows under it.
                className="flex min-h-10 w-full items-center gap-3 border-t border-b-2 border-t-border-default border-b-accent-solid bg-brand-soft px-row-x py-2 text-left first:border-t-0 enabled:hover:bg-surface-sunken"
              >
                <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-md bg-surface-raised text-content-brand shadow-sm">
                  <PositionGlyph position={p} className="size-3.5" />
                </span>
                <span className="min-w-0 flex-1 truncate text-base font-semibold text-content-default">
                  {p.name}
                </span>
                {p.open === 0 && p.overridden && p.needed === 0 && (
                  <Chip tone="neutral">Not this week</Chip>
                )}
              </button>
              {(p.people.length > 0 || p.open > 0) && (
                <ul className="divide-y divide-border-subtle">
                  {p.people.map((h) => (
                    <li key={h.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setPersonId(h.id)
                          setPersonOpen(true)
                        }}
                        className={`${teamPersonRow} hover:bg-surface-sunken`}
                      >
                        <PersonAvatar
                          personId={h.personId}
                          name={h.name}
                          avatarUrl={h.avatarUrl}
                          status={h.status}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-base text-content-default">
                            {h.name}
                          </span>
                          {servedLabel(plan.times, h.timeIds) && (
                            <span className="block truncate text-sm text-content-muted">
                              {servedLabel(plan.times, h.timeIds)}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                  {p.open > 0 && (
                    <li>
                      {editable ? (
                        <button
                          type="button"
                          onClick={() => setAsking(p.id)}
                          aria-label={`${p.open} needed as ${p.name}: ask someone`}
                          className={`${teamPersonRow} hover:bg-surface-sunken`}
                        >
                          <NeededRow count={p.open} />
                        </button>
                      ) : (
                        <div
                          className={teamPersonRow}
                          aria-label={`${p.open} needed as ${p.name}`}
                          role="group"
                        >
                          <NeededRow count={p.open} />
                        </div>
                      )}
                    </li>
                  )}
                </ul>
              )}
            </section>
          ))}
          {positions.length === 0 && (
            <p className="px-4 py-3 text-sm text-content-muted">No positions on this team.</p>
          )}
        </Card>
      ))}
      {roster.schedulesAny && (
        <AskPanel
          planId={planId}
          planDate={plan.date}
          people={plan.people}
          times={plan.times}
          positionId={asking}
          positions={roster.schedulablePositions}
          onClose={() => setAsking(null)}
        />
      )}
      <PersonPanel
        planId={planId}
        times={plan.times}
        person={person}
        scheduling={person ? roster.schedulesTeam(person.teamId) : false}
        open={personOpen && person !== undefined}
        onClose={() => setPersonOpen(false)}
      />
    </section>
  )
}

/**
 * A person's row under a position: a row's rhythm, indented to start under
 * the position's name (its 24px tile and the gap after it), not its tile.
 */
const teamPersonRow = 'flex min-h-12 w-full items-center gap-3 py-3 pr-4 pl-13 text-left'

/** A place still open, where a person would be: the dashed circle, and Needed. */
function NeededRow({ count }: { count: number }) {
  return (
    <>
      <NeededAvatar count={count} />
      <span className="min-w-0 flex-1 truncate text-base text-content-muted">Needed</span>
    </>
  )
}

/** A team at a glance: confirmed, not answered yet, rejected. */
function TeamCounts({
  counts,
}: {
  counts: { confirmed: number; pending: number; declined: number }
}) {
  const stats = [
    {
      label: 'Confirmed',
      value: counts.confirmed,
      icon: faCircleCheck,
      tone: 'text-success-solid',
    },
    { label: 'Pending', value: counts.pending, icon: faHourglassHalf, tone: 'text-warning-solid' },
    { label: 'Rejected', value: counts.declined, icon: faCircleXmark, tone: 'text-danger-solid' },
  ]
  return (
    <span className="flex items-center gap-3 pr-2">
      {stats.map((stat) => (
        <span
          key={stat.label}
          title={`${stat.label}: ${stat.value}`}
          aria-label={`${stat.label}: ${stat.value}`}
          role="img"
          className={`inline-flex items-center gap-1 text-sm font-semibold tabular-nums ${
            stat.value === 0 ? 'text-content-subtle' : 'text-content-default'
          }`}
        >
          <Icon icon={stat.icon} className={`size-3.5 ${stat.value === 0 ? '' : stat.tone}`} />
          {stat.value}
        </span>
      ))}
    </span>
  )
}

type PlanPerson = NonNullable<ReturnType<typeof usePlanEditor>['plan']>['people'][number]

/**
 * One person on the plan: where they stand and how to reach them (contact
 * details only when ForServices returns them — 13-ACCESS), and for a
 * Scheduler a reminder and taking them off.
 */
function PersonPanel({
  planId,
  times,
  person,
  scheduling: canSchedule,
  open,
  onClose,
}: {
  planId: string
  times: Plan['times']
  person: PlanPerson | undefined
  /** They schedule this person's team here (Scheduler on the plan's service type, or its leader). */
  scheduling: boolean
  open: boolean
  onClose: () => void
}) {
  const scheduling = usePlanScheduling(planId)
  const card = useServicesPerson(open && person ? person.personId : null)
  const removeLater = useUndoableRemove()
  const [reminded, setReminded] = useState<string | null>(null)
  if (!person) return null
  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={person.name}
      // Their answer is a fact about them here, beside what they were asked for.
      description={`${person.positionName} · ${responseOf(person.status).label}`}
      footer={
        canSchedule ? (
          <Button
            variant="quietDanger"
            icon={faUserMinus}
            className="-ml-4"
            onClick={() => {
              onClose()
              removeLater({
                id: person.id,
                title: `Took ${person.name} off this plan`,
                description: `No longer ${person.positionName}. They aren’t told: let them know yourself.`,
                commit: () => scheduling.unassign.mutateAsync(person.id),
              })
            }}
          >
            Take off this plan
          </Button>
        ) : undefined
      }
    >
      <ProfileRow
        personId={person.personId}
        name={person.name}
        avatarUrl={person.avatarUrl}
        status={person.status}
      />
      {person.declineReason && (
        <PanelSection title="Why">
          <p className="text-sm text-content-muted">“{person.declineReason}”</p>
        </PanelSection>
      )}
      {card.data?.contact && <ContactRows contact={card.data.contact} />}
      {canSchedule && times.length > 1 && (
        <PanelSection title="Serves at">
          <TimeChoice
            times={times}
            value={person.timeIds}
            disabled={scheduling.setTimes.isPending}
            onChange={(timeIds) => scheduling.setTimes.mutate({ planPersonId: person.id, timeIds })}
          />
        </PanelSection>
      )}
      {canSchedule && person.status === 'requested' && (
        <PanelSection title="Reminder">
          <Button
            variant="secondary"
            icon={reminded === person.id ? faCheck : faBell}
            pending={scheduling.remind.isPending}
            pendingLabel="Reminding…"
            disabled={reminded === person.id}
            onClick={() =>
              scheduling.remind.mutate(person.id, {
                onSuccess: () => {
                  setReminded(person.id)
                  notify({
                    title: `Reminded ${person.name}`,
                    description: `They were sent the request to serve as ${person.positionName} again.`,
                  })
                },
              })
            }
          >
            {reminded === person.id ? 'Reminder sent' : 'Remind'}
          </Button>
        </PanelSection>
      )}
      <ErrorNote
        error={scheduling.remind.error ?? scheduling.setTimes.error}
        onRetry={() => {
          if (scheduling.remind.error) scheduling.remind.mutate(person.id)
          else if (scheduling.setTimes.variables)
            scheduling.setTimes.mutate(scheduling.setTimes.variables)
        }}
        safe="Nothing else about their place on the plan changed."
      />
    </SidePanel>
  )
}

/** How to reach someone: each phone and email a link, the address as a line. */
function ContactRows({ contact }: { contact: NonNullable<ServicesPerson['contact']> }) {
  const address = contact.address
    ? [contact.address.street, contact.address.city, contact.address.postalCode]
        .filter(Boolean)
        .join(', ')
    : ''
  const row =
    'flex min-h-11 items-center gap-3 rounded-lg px-2 text-base text-content-default no-underline hover:bg-surface-sunken'
  if (contact.phones.length === 0 && contact.emails.length === 0 && !address) return null
  return (
    <PanelSection title="Contact">
      <ul className="-mx-2">
        {contact.phones.map((phone) => (
          <li key={phone.number}>
            <a href={`tel:${phone.number}`} className={row}>
              <Icon icon={faPhone} className="size-4 text-content-brand" />
              <span className="min-w-0 flex-1 truncate tabular-nums">{phone.number}</span>
              {phone.label && <span className="text-sm text-content-muted">{phone.label}</span>}
            </a>
          </li>
        ))}
        {contact.emails.map((email) => (
          <li key={email.address}>
            <a href={`mailto:${email.address}`} className={row}>
              <Icon icon={faEnvelope} className="size-4 text-content-brand" />
              <span className="min-w-0 flex-1 truncate">{email.address}</span>
            </a>
          </li>
        ))}
        {address && (
          <li className="flex min-h-11 items-center gap-3 px-2 text-base text-content-default">
            <Icon icon={faLocationDot} className="size-4 text-content-brand" />
            <span className="min-w-0 flex-1">{address}</span>
          </li>
        )}
      </ul>
    </PanelSection>
  )
}

/**
 * Asking someone to serve: the position, the people on its team, anyone else.
 * Each row says where it stands — Ask, Asking…, Asked — and each request
 * sent says so, with Undo. Only a real clash (409: away that day, or already
 * serving then) offers Ask anyway; any other failure says why, with Retry.
 */
function AskPanel({
  planId,
  planDate,
  people,
  times,
  positionId,
  positions,
  onClose,
}: {
  planId: string
  planDate: string
  people: PlanPerson[]
  times: Plan['times']
  positionId: string | null
  positions: {
    id: string
    name: string
    teamName: string
    neededPerPlan: number
    needed: number
    overridden: boolean
    members: { personId: string; name: string }[]
  }[]
  onClose: () => void
}) {
  const scheduling = usePlanScheduling(planId)
  const [chosen, setChosen] = useState('')
  // The times the next person asked will serve: all of them unless narrowed.
  const [timeIds, setTimeIds] = useState<string[]>([])
  const servingAt = timeIds.length > 0 ? timeIds : times.map((time) => time.id)
  const [term, setTerm] = useState('')
  const [asked, setAsked] = useState<string[]>([])
  const [asking, setAsking] = useState<string | null>(null)
  const [conflict, setConflict] = useState<{
    personId: string
    name: string
    message: string
  } | null>(null)
  const [failed, setFailed] = useState<{ personId: string; name: string; error: unknown } | null>(
    null,
  )
  const { results } = usePeopleSearch(term)
  // Opening on a position chooses it; the choice is kept while the panel slides away.
  const [openedFor, setOpenedFor] = useState<string | null>(null)
  if (positionId !== null && positionId !== openedFor) {
    setOpenedFor(positionId)
    setChosen(positionId)
    setConflict(null)
    setFailed(null)
    setAsked([])
    setTimeIds([])
  }
  const position = positions.find((p) => p.id === chosen) ?? positions[0]
  if (!position) return null

  const ask = (personId: string, name: string, override = false) => {
    setAsking(personId)
    setFailed(null)
    scheduling.assign.mutate(
      {
        positionId: position.id,
        personId,
        override,
        ...(servingAt.length < times.length && { timeIds: servingAt }),
      },
      {
        onSuccess: (plan) => {
          setConflict(null)
          setAsked((list) => [...list, personId])
          const place = plan?.people.find(
            (p) =>
              p.personId === personId && p.positionId === position.id && p.status === 'requested',
          )
          notify({
            title: `Asked ${name} to serve as ${position.name}`,
            description: `${prettyDate(planDate)}. They get a request to accept or decline.`,
            action: place
              ? {
                  label: 'Undo',
                  run: () => {
                    scheduling.unassign.mutate(place.id)
                    setAsked((list) => list.filter((id) => id !== personId))
                  },
                }
              : undefined,
          })
        },
        onError: (error) => {
          if ((error as { status?: unknown }).status === 409) {
            const value = (error as { value?: { detail?: string } }).value
            setConflict({
              personId,
              name,
              message: value?.detail ?? `${name} can’t serve then.`,
            })
          } else setFailed({ personId, name, error })
        },
        onSettled: () => setAsking(null),
      },
    )
  }
  // Already on the plan in this position: nothing to ask.
  const already = (personId: string) =>
    people.some(
      (h) => h.personId === personId && h.positionId === position.id && h.status !== 'declined',
    )
  const personButton = (personId: string, name: string) => {
    const on = already(personId)
    const done = asked.includes(personId)
    const busy = asking === personId
    return (
      <button
        key={personId}
        type="button"
        disabled={done || on || busy}
        aria-busy={busy || undefined}
        onClick={() => ask(personId, name)}
        className={`${listRow} flex w-full items-center gap-3 px-panel-x text-left transition-colors duration-(--duration-fast) enabled:hover:bg-surface-sunken enabled:active:bg-surface-sunken`}
      >
        <PersonAvatar personId={personId} name={name} avatarUrl={null} size="md" />
        <span className="min-w-0 flex-1 truncate text-base text-content-default">{name}</span>
        <span
          className={`inline-flex shrink-0 items-center gap-1.5 text-sm font-medium ${
            on ? 'text-content-muted' : done ? 'text-success-content' : 'text-content-brand'
          }`}
        >
          {on ? (
            'On this plan'
          ) : busy ? (
            <>
              <InlineSpinner className="size-3.5" />
              Asking…
            </>
          ) : done ? (
            <>
              <Icon icon={faCheck} className="size-3.5" />
              Asked
            </>
          ) : (
            'Ask'
          )}
        </span>
      </button>
    )
  }

  return (
    <SidePanel
      open={positionId !== null}
      onOpenChange={(next) => {
        if (!next) {
          onClose()
          setOpenedFor(null)
        }
      }}
      title="Ask someone to serve"
      description={`${prettyDate(planDate)}. They are sent a request to accept or decline.`}
    >
      <PanelSection title="Position">
        <Select
          value={position.id}
          aria-label="Position"
          onChange={(event) => setChosen(event.target.value)}
        >
          {positions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.teamName} — {p.name}
            </option>
          ))}
        </Select>
      </PanelSection>
      <PanelSection
        title="Needed on this plan"
        hint={
          position.overridden
            ? `Usually ${position.neededPerPlan}.`
            : 'Its usual number; change it for this plan only.'
        }
      >
        <NeededStepper
          value={position.needed}
          disabled={scheduling.setNeed.isPending}
          onChange={(needed) => scheduling.setNeed.mutate({ positionId: position.id, needed })}
        />
        <ErrorNote
          error={scheduling.setNeed.error}
          onRetry={() =>
            scheduling.setNeed.variables && scheduling.setNeed.mutate(scheduling.setNeed.variables)
          }
        />
      </PanelSection>
      {times.length > 1 && (
        <PanelSection title="Serving at">
          <TimeChoice times={times} value={servingAt} onChange={setTimeIds} />
        </PanelSection>
      )}
      {conflict && (
        <div
          role="alert"
          className="space-y-2 rounded-control border border-warning-solid/40 bg-warning-soft p-3 text-sm text-warning-content"
        >
          <p className="font-medium">Check before asking {conflict.name}</p>
          <p>{conflict.message}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              pending={asking === conflict.personId}
              pendingLabel="Asking…"
              onClick={() => ask(conflict.personId, conflict.name, true)}
            >
              Ask anyway
            </Button>
            <Button variant="ghost" onClick={() => setConflict(null)}>
              Choose someone else
            </Button>
          </div>
        </div>
      )}
      {failed && (
        <ErrorNote
          error={failed.error}
          onRetry={() => ask(failed.personId, failed.name)}
          retrying={asking === failed.personId}
          safe={`${failed.name} wasn’t asked; nobody was sent anything.`}
        />
      )}
      {position.members.length > 0 && (
        <PanelSection title={`On the ${position.teamName} team`}>
          <div className="-mx-panel-x divide-y divide-border-subtle">
            {position.members.map((m) => personButton(m.personId, m.name))}
          </div>
        </PanelSection>
      )}
      <PanelSection title="Someone else">
        <Input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Search people"
          aria-label="Search people"
        />
        <div className="-mx-panel-x divide-y divide-border-subtle">
          {results.slice(0, 6).map((p) => personButton(p.id, `${p.firstName} ${p.lastName}`))}
        </div>
      </PanelSection>
    </SidePanel>
  )
}

/** How many a position needs on this plan: one less, one more. */
function NeededStepper({
  value,
  disabled,
  onChange,
}: {
  value: number
  disabled: boolean
  onChange: (needed: number) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <IconButton
        icon={faMinus}
        label="One fewer"
        variant="secondary"
        disabled={disabled || value <= 0}
        onClick={() => onChange(value - 1)}
      />
      <span className="w-8 text-center text-2xl font-semibold tabular-nums text-content-default">
        {value}
      </span>
      <IconButton
        icon={faPlus}
        label="One more"
        variant="secondary"
        disabled={disabled || value >= 50}
        onClick={() => onChange(value + 1)}
      />
    </div>
  )
}
